import { useBuildingChatbotMutation } from "ApiLayer/Chatbot/queryBuildingChatbot";
import {
    useBuildingChatHistoryQuery,
    useClearBuildingChatHistoryMutation,
    useExportBuildingChatHistory,
} from "ApiLayer/Chatbot/queryBuildingChatHistory";
import usePortfolioChatbotMutation from "ApiLayer/Chatbot/queryPortfolioChatbot";
import {
    chatbotAgents,
    chatbotWelcomeMessages,
    Message,
} from "components/organisms/Chatbot/ChatbotSidebar/ChatbotSidebar.constants";
import { scrollToBottom } from "hooks/UI/useScrollToBottom";
import { useEffect, useRef, useState } from "react";
import { getIndefiniteArticle } from "utils/helpers";

interface UseChatbotProps {
    page: PageWithChatbot;
    sidebarOpen: boolean;
    buildingModelUid: string | null;
}

const useChatbot = ({ page, buildingModelUid }: UseChatbotProps) => {
    const chatbotWelcomeMessage = chatbotWelcomeMessages[page];

    // hooks
    const { mutate: sendMessagePortfolio, isLoading: isLoadingPortfolio } = usePortfolioChatbotMutation();
    const { mutate: sendBuildingMessage, isLoading: isLoadingBuilding } = useBuildingChatbotMutation();
    const {
        data: chatHistory,
        isLoading: isHistoryLoading,
        isSuccess: isHistorySuccess,
    } = useBuildingChatHistoryQuery(buildingModelUid);
    const { mutate: clearChatHistory, isLoading: isClearingHistory } = useClearBuildingChatHistoryMutation();
    const { mutate: exportChatHistory, isLoading: isExportingHistory } = useExportBuildingChatHistory();

    // State
    const [agent, setAgent] = useState<string>(chatbotAgents[0].id);
    const [holdingPeriod, setHoldingPeriod] = useState(10);
    const [messages, setMessages] = useState<Message[]>([chatbotWelcomeMessage]);
    const [currentMessage, setCurrentMessage] = useState("");
    const messagesEndRef = useRef<null | HTMLDivElement>(null);

    const isLoading = isLoadingBuilding || isLoadingPortfolio;

    const sendBuildingMessageAndUpdate = (message: string, agentType: string, afterSuccess?: () => void) => {
        if (!buildingModelUid) return;
        const messageId = crypto.randomUUID();
        setMessages((prev) => [...prev, { id: messageId, text: "", type: "receive", isThinking: true }]);

        sendBuildingMessage(
            { message, buildingModelUid, agent: agentType },
            {
                onSuccess: (data: any) => {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === messageId
                                ? {
                                      ...m,
                                      isThinking: false,
                                      text: data.response,
                                      modelUsed: data.model_used,
                                      isGenericResponse: agentType === "generic",
                                      thoughts: data.thoughts,
                                      thinkingDuration: data.thinking_duration,
                                      exportRunUid: data.run_uid,
                                      agentId: agentType,
                                  }
                                : m
                        )
                    );
                    afterSuccess?.();
                },
                onError: () => {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === messageId
                                ? {
                                      ...m,
                                      isThinking: false,
                                      text: "An error occurred. Please try again.",
                                      activeToolCalls: [],
                                  }
                                : m
                        )
                    );
                },
            }
        );
    };

    const getAgentDisplayName = (agentId: string): string => {
        const thisAgent = chatbotAgents.find((a) => a.id === agentId);
        return thisAgent?.displayValue || agentId;
    };

    const handleSendMessage = async () => {
        if (isHistoryLoading) return;

        const sentAgentType = agent;

        const sentMessage = !currentMessage.trim()
            ? `Generate ${getIndefiniteArticle(getAgentDisplayName(agent))} ${getAgentDisplayName(agent)} report`
            : currentMessage;

        setMessages((prev) => [...prev, { id: crypto.randomUUID(), text: sentMessage, type: "send" }]);
        setCurrentMessage("");

        setTimeout(() => {
            if (page === "building") {
                if (!buildingModelUid) {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: crypto.randomUUID(),
                            text: "Error: Building ID not found. Please refresh the page and try again.",
                            type: "receive",
                        },
                    ]);
                    return;
                }
                sendBuildingMessageAndUpdate(sentMessage, sentAgentType, () => {
                    if (sentAgentType !== "generic") setAgent("generic");
                });
            } else if (page === "portfolio") {
                sendMessagePortfolio(
                    { message: sentMessage, holdPeriod: holdingPeriod },
                    {
                        onSuccess: (response: {
                            response: string;
                            model_used?: string;
                            thoughts?: string;
                            thinking_duration?: number;
                            run_uid?: string;
                        }) => {
                            setMessages((prev) => [
                                ...prev,
                                {
                                    id: crypto.randomUUID(),
                                    text: response.response,
                                    type: "receive",
                                    modelUsed: response.model_used,
                                    isGenericResponse: sentAgentType === "generic",
                                    thoughts: response.thoughts,
                                    thinkingDuration: response.thinking_duration,
                                },
                            ]);
                            if (sentAgentType !== "generic") {
                                setAgent("generic");
                            }
                        },
                        onError: (response: any) => {
                            setMessages((prev) => [
                                ...prev,
                                {
                                    id: crypto.randomUUID(),
                                    text:
                                        response.message ||
                                        "An error occurred. Read the message at the top of the screen for more information",
                                    type: "receive",
                                },
                            ]);
                        },
                    }
                );
            } else {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: crypto.randomUUID(),
                        text: `Invalid page type: ${page}. Please refresh the page and try again.`,
                        type: "receive",
                    },
                ]);
            }
        }, 0);
    };

    const handleAgentSelect = (agentId: string) => {
        setAgent(agentId);
        if (page !== "building" || !buildingModelUid) return;
        const sentMessage = `Generate ${getIndefiniteArticle(getAgentDisplayName(agentId))} ${getAgentDisplayName(agentId)} report`;
        setMessages((prev) => [...prev, { id: crypto.randomUUID(), text: sentMessage, type: "send" }]);
        setCurrentMessage("");
        sendBuildingMessageAndUpdate(sentMessage, agentId, () => setAgent("generic"));
    };

    const handleClearHistory = (callbacks: { onSuccess: () => void; onError: () => void }) => {
        if (!buildingModelUid) return;
        clearChatHistory(buildingModelUid, {
            onSuccess: () => {
                setMessages([chatbotWelcomeMessage]);
                callbacks.onSuccess();
            },
            onError: (error) => {
                console.error("Failed to clear chat history:", error);
                callbacks.onError();
            },
        });
    };

    const handleDownloadHistory = () => {
        if (!buildingModelUid || isExportingHistory) return;
        exportChatHistory(buildingModelUid, {
            onSuccess: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `chat_conversation_${buildingModelUid}.html`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            onError: (error) => {
                console.error("Failed to export chat history:", error);
            },
        });
    };

    useEffect(() => {
        if (!isHistoryLoading) {
            scrollToBottom(messagesEndRef);
        }
    }, [messages, isHistoryLoading, isLoading]);

    // Populate messages from query data once history loads
    useEffect(() => {
        if (isHistorySuccess && chatHistory && page === "building") {
            const loadedMessages: Message[] = chatHistory.map((msg: any) => ({
                id: crypto.randomUUID(),
                text: msg.parts[0].text,
                type: msg.role === "user" ? "send" : "receive",
                thoughts: msg.thoughts,
                thinkingDuration: msg.thinking_duration,
                exportRunUid: msg.agent_run_uid,
                agentId: msg.agent_name,
            }));
            setMessages([chatbotWelcomeMessage, ...loadedMessages]);
        }
    }, [isHistorySuccess, chatHistory, page, chatbotWelcomeMessage]);

    return {
        messages,
        setMessages,
        currentMessage,
        setCurrentMessage,
        handleSendMessage,
        handleAgentSelect,
        handleClearHistory,
        handleDownloadHistory,
        isLoading,
        isLoadingPortfolio,
        isHistoryLoading,
        isClearingHistory,
        isExportingHistory,
        messagesEndRef,
        chatbotWelcomeMessage,
        holdingPeriod,
        setHoldingPeriod,
    };
};

export default useChatbot;
